import { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';

/**
 * Dropzone component for file uploads with drag-and-drop functionality
 *
 * @param {Object} props - Component props
 * @param {Function} props.onFilesAccepted - Callback function that receives the updated files array whenever files are added or removed
 * @param {Function} [props.onUpload=null] - Optional async callback to upload files. Receives (files[], abortSignal). When provided, files auto-upload on drop.
 * @param {Function} [props.onUploadSuccess=null] - Optional callback fired when files upload successfully. Receives (files[]).
 * @param {Function} [props.onDelete=null] - Optional async callback when removing individual files. Receives (file).
 * @param {boolean} [props.isDeleting=false] - Optional loading state for delete operations
 * @param {boolean} [props.disabled=false] - Disables file uploads when true
 * @param {number} [props.maxFiles=1] - Maximum number of files that can be uploaded (default: 1)
 * @param {Object} [props.accept={'image/*': []}] - Object defining accepted file types in react-dropzone format (default: all image types)
 * @param {string} [props.dropzoneText='Drag & drop files here, or click to select files'] - Text displayed in the dropzone area
 * @param {any} props.clearFiles - Dependency value that triggers clearing all files when changed (typically a counter or timestamp)
 */
const Dropzone = ({
  onFilesAccepted,
  onUpload = null,
  onUploadSuccess = null,
  onDelete = null,
  isDeleting = false,
  disabled = false,
  maxFiles = 1,
  accept = { 'image/*': [] },
  dropzoneText = 'Drag & drop files here, or click to select files',
  clearFiles,
}) => {
  /**
   * @state {Array} files - Array of file objects
   * When onUpload is provided: { id: string, file: File, status: 'pending' | 'uploading' | 'success' | 'error', error?: string }
   * When onUpload is not provided: raw File objects (backwards compatible)
   */
  const [files, setFiles] = useState([]);
  const [limitError, setLimitError] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const abortControllerRef = useRef(null);

  const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  /**
   * Uploads files
   */
  const uploadFiles = useCallback(
    async (fileEntries) => {
      if (!onUpload || fileEntries.length === 0) return;

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const fileIds = fileEntries.map((f) => f.id);

      // Mark as uploading
      setFiles((prev) =>
        prev.map((f) => (fileIds.includes(f.id) ? { ...f, status: 'uploading' } : f))
      );

      try {
        await onUpload(
          fileEntries.map((f) => f.file),
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        // Success
        setFiles((prev) =>
          prev.map((f) =>
            fileIds.includes(f.id) && f.status === 'uploading' ? { ...f, status: 'success' } : f
          )
        );

        // Notify parent of successful uploads
        onUploadSuccess?.(fileEntries.map((f) => f.file));
      } catch (err) {
        if (err.name === 'AbortError' || abortController.signal.aborted) return;

        // Error
        setFiles((prev) =>
          prev.map((f) =>
            fileIds.includes(f.id) && f.status === 'uploading'
              ? { ...f, status: 'error', error: err.message || 'Upload failed' }
              : f
          )
        );
      } finally {
        abortControllerRef.current = null;
      }
    },
    [onUpload, onUploadSuccess]
  );

  /**
   * Callback function triggered when files are dropped or selected
   */
  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      // Calculate how many files we can still accept
      const availableSlots = maxFiles - files.length;
      const totalAttempted = acceptedFiles.length + fileRejections.length;

      // Clear any existing error
      setLimitError(null);

      // Check if user tried to upload more files than available slots
      if (totalAttempted > availableSlots && availableSlots >= 0) {
        setLimitError(`Maximum of ${maxFiles} file${maxFiles !== 1 ? 's' : ''} allowed.`);

        // Auto-clear error after 5 seconds
        setTimeout(() => setLimitError(null), 5000);
      }

      if (!onUpload) {
        // Backwards compatible mode - just store raw File objects
        const filesToAdd = acceptedFiles.slice(0, availableSlots);
        const newFiles = [...files, ...filesToAdd];
        setFiles(newFiles);
        onFilesAccepted(newFiles);
        return;
      }

      // Enhanced mode with upload
      // Filter out "too many files" rejections - only show actual validation errors
      const rejectedEntries = fileRejections
        .filter(({ errors }) => !errors.some((e) => e.code === 'too-many-files'))
        .map(({ file, errors }) => ({
          id: generateId(),
          file,
          status: 'error',
          error: errors.map((e) => e.message).join(', '),
        }));

      // Only take files up to available slots - excess files are silently ignored
      const filesToAdd = acceptedFiles.slice(0, availableSlots);

      const newEntries = filesToAdd.map((file) => ({
        id: generateId(),
        file,
        status: 'pending',
      }));

      const allNewEntries = [...newEntries, ...rejectedEntries];

      setFiles((prev) => {
        const combined = [...prev, ...allNewEntries];
        onFilesAccepted(combined.map((f) => f.file));
        return combined;
      });

      // Trigger upload only for accepted files
      if (newEntries.length > 0) {
        uploadFiles(newEntries);
      }
    },
    [files, maxFiles, onFilesAccepted, onUpload, uploadFiles]
  );

  /**
   * Cancels in-progress upload
   */
  const handleCancelUpload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setFiles((prev) => {
      const updated = prev.filter((f) => f.status !== 'uploading');
      onFilesAccepted(updated.map((f) => f.file));
      return updated;
    });
  }, [onFilesAccepted]);

  /**
   * Removes a file
   */
  const handleRemoveFile = useCallback(
    async (fileId) => {
      if (onUpload) {
        const fileEntry = files.find((f) => f.id === fileId);
        if (!fileEntry) return;

        // If uploading, cancel
        if (fileEntry.status === 'uploading') {
          handleCancelUpload();
          return;
        }

        // Track which file is being deleted
        setDeletingFileId(fileId);

        // Call onDelete if provided (for individual file removal)
        if (onDelete) {
          try {
            await onDelete(fileEntry.file);
          } catch (err) {
            console.error('onDelete failed:', err);
            setDeletingFileId(null);
            return;
          }
        }

        setFiles((prev) => {
          const updated = prev.filter((f) => f.id !== fileId);
          onFilesAccepted(updated.map((f) => f.file));
          return updated;
        });

        setDeletingFileId(null);
      } else {
        // Backwards compatible mode - fileId is index
        const index = fileId;
        const removedFile = files[index];

        // Track which file is being deleted
        setDeletingFileId(index);

        if (onDelete) {
          try {
            await onDelete(removedFile);
          } catch (err) {
            console.error('onDelete failed:', err);
            setDeletingFileId(null);
            return;
          }
        }

        const updatedFiles = files.filter((_, i) => i !== index);
        setFiles(updatedFiles);
        onFilesAccepted(updatedFiles);
        setDeletingFileId(null);
      }
    },
    [files, onUpload, onDelete, onFilesAccepted, handleCancelUpload]
  );

  /**
   * Retry failed files
   */
  const handleRetry = useCallback(
    (fileId) => {
      const fileEntry = files.find((f) => f.id === fileId);
      if (!fileEntry || fileEntry.status !== 'error') return;

      const updatedEntry = { ...fileEntry, status: 'pending', error: undefined };

      setFiles((prev) => prev.map((f) => (f.id === fileId ? updatedEntry : f)));
      uploadFiles([updatedEntry]);
    },
    [files, uploadFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: onUpload ? maxFiles - files.length : maxFiles,
    disabled: disabled || files.length >= maxFiles,
  });

  /**
   * Effect that clears all files when the clearFiles dependency changes
   */
  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setFiles([]);
  }, [clearFiles]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon color="error" sx={{ mr: 1, fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const isFull = files.length >= maxFiles;
  const isDropzoneDisabled = disabled || isFull;

  const renderFileItem = (fileEntry, index) => {
    if (onUpload) {
      const { id, file, status, error } = fileEntry;
      const isThisFileDeleting = isDeleting && deletingFileId === id;

      return (
        <ListItem
          key={id}
          secondaryAction={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {status === 'error' && !isThisFileDeleting && (
                <IconButton edge="end" onClick={() => handleRetry(id)} size="small">
                  <ReplayIcon />
                </IconButton>
              )}
              <IconButton
                edge="end"
                onClick={() => handleRemoveFile(id)}
                size="small"
                disabled={isThisFileDeleting}
              >
                {isThisFileDeleting ? (
                  <CircularProgress size={20} />
                ) : status === 'uploading' ? (
                  <CancelIcon />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </Box>
          }
          sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 8 }}>
            {getStatusIcon(status)}
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {file.name}
            </Typography>
          </Box>
          {status === 'uploading' && <LinearProgress sx={{ width: '100%', mt: 1 }} />}
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </ListItem>
      );
    } else {
      // Backwards compatible mode
      const isThisFileDeleting = isDeleting && deletingFileId === index;

      return (
        <ListItem
          key={index}
          secondaryAction={
            <IconButton
              edge="end"
              onClick={() => handleRemoveFile(index)}
              disabled={isThisFileDeleting}
            >
              {isThisFileDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            </IconButton>
          }
        >
          {fileEntry.name}
        </ListItem>
      );
    }
  };

  return (
    <Box sx={{ padding: '.5rem 0' }}>
      <Box
        {...getRootProps()}
        sx={{
          p: '.875rem',
          border: '2px dashed #aaa',
          textAlign: 'center',
          cursor: isDropzoneDisabled ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? '#f0f0f0' : 'background.paper',
          opacity: isDropzoneDisabled ? 0.6 : 1,
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body1">
          {isFull ? `Maximum files reached (${maxFiles})` : dropzoneText}
        </Typography>
      </Box>
      {limitError && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography
            variant="caption"
            color="error"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {limitError}
          </Typography>
        </Box>
      )}
      {files.length > 0 && (
        <List sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {files.map((fileEntry, index) => renderFileItem(fileEntry, index))}
        </List>
      )}
    </Box>
  );
};

export default Dropzone;
